import { getOrCreateMainLogger } from "../logger/main";
import fse from "fs-extra";
import path from "path";
const logger = getOrCreateMainLogger();

export type FinsembleConfig = {
	folderName: string;
	chromiumFlags: Record<string, string>;
	manifestUrl: string;
	loggingMaxFiles?: number | string;
	loggingMaxSize?: string;
	updateFeedUrl?: string;
	deleteUserDataOnUninstall?: boolean;
	sanitizingRegExes?: string[];
};

export const defaultConfig: FinsembleConfig = Object.freeze({
	folderName: "finsemble-electron-adapter",
	chromiumFlags: {},
	manifestUrl: "http://localhost:3375/configs/application/manifest-local.json",
	loggingMaxFiles: 3,
	loggingMaxSize: "5m",
});

/**
 * "getConfig" will contain the config stored alongside the asar when Finsemble is packaged.
 */
let config: FinsembleConfig | undefined;

/**
 * Recursively finds closest parent directory containing a package.json file.
 * @returns
 */
export const getPackageRoot: (dirname?: string) => string = (dirname = __dirname) =>
	fse.existsSync(path.resolve(dirname, "package.json")) ? dirname : getPackageRoot(path.resolve(dirname, ".."));

/**
 * Path of electron-adapter package
 * @returns
 */
export const getConfigPath = () => path.resolve(getPackageRoot(), "deploy", "config.json");

/**
 * FEA instances that have been created with an installer will have an embedded file called
 * config.json. This file is created by the installer and embedded in the asar file.
 */
export const getConfig = ({
	throws = false,
	suppressLog = false,
}: { throws?: boolean; suppressLog?: boolean } = {}) => {
	if (!config) {
		try {
			config = fse.readJsonSync(getConfigPath());
		} catch (error) {
			if (!suppressLog) {
				logger.error("An error occurred while reading config:", error);
			}
			if (throws) {
				throw error;
			}
		}
	}
	// ts thinks this could still be undefined, so force a cast
	return config as FinsembleConfig;
};

/**
 * Writes config to the appropriate json file
 * @param configToWrite
 */
export const writeConfig = (configToWrite: FinsembleConfig) => {
	const configPath = getConfigPath();
	fse.ensureDirSync(path.dirname(configPath));
	fse.writeJSONSync(configPath, configToWrite, { spaces: "\t" });
};

/**
 * Deletes the config.  Currently only used by unit tests.
 */
export const deleteConfig = () => {
	try {
		fse.rmSync(getConfigPath());
	} finally {
		config = undefined; // Clear cache regardless of whether the file existed
	}
};
